import { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataTable } from './DataTable';
import { DropdownOptionsFilter, FiltersBar, CheckboxListFilter, StatusIconConfig } from './FiltersBar';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { colors } from '@src/styles';

const SCRIPT_STATUS_ICON_CONFIG: Record<string, StatusIconConfig> = {
  PENDING: { icon: HourglassEmptyIcon, color: colors.brand.textSecondary },
  RUNNING: { icon: SyncIcon, color: colors.status.pending },
  BLOCKED: { icon: BlockIcon, color: colors.status.rejected },
  FINISHED: { icon: CheckCircleIcon, color: colors.status.concluded },
  FAILED: { icon: ErrorIcon, color: colors.primary.warning },
};
import { FiltersWrapper } from './FiltersWrapper';
import { DateRangeFilter } from './DateRangeFilter';
import { ScriptsListSkeleton } from './ScriptsListSkeleton';
import { ScriptsDTO } from '@src/services/tools/modules/v1/scripts/dto/brain-scripts.getById.response.dto';
import { getScripts, ScriptFilters, getTeamMembers, buildUserNamesFromTeamMembers } from '../hooks/scripts';
import { getOrganizationsNames } from '../hooks/organizations';
import { getScriptsListColumns } from '../utils/getScriptsListColumns';
import { useError } from '@src/contexts/ErrorContext';
import { useScriptsContext } from '../context/ScriptsContext';
import { TeamMemberItemDTO } from '@src/services/brain/modules/v1/team-members/dto/brain-team-members.get.response.dto';
import { CreateCanonicalScriptModal } from './CreateCanonicalScriptModal';
import { primaryButtonSX, titleFontFamily } from '../scriptsStyles';

export const ScriptsListView = () => {
  const { handleScriptClick: onScriptClick } = useScriptsContext();
  const [scripts, setScripts] = useState<ScriptsDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchName, setSearchName] = useState('');
  const [searchStatus, setSearchStatus] = useState<string[]>([]);
  const [searchOrganization, setSearchOrganization] = useState('');
  const [searchAuthor, setSearchAuthor] = useState('');
  const [searchApprover, setSearchApprover] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMemberItemDTO[]>([]);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(true);
  const [organizations, setOrganizations] = useState<Record<string, string>>({});
  const { setError } = useError();
  const filtersChangedRef = useRef<boolean>(false);

  useEffect(() => {
    filtersChangedRef.current = true;
    setCurrentPage(1);
  }, [searchName, searchStatus, searchOrganization, searchAuthor, searchApprover, startDate, endDate]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoadingTeamMembers(true);
      try {
        const [members, orgs] = await Promise.all([getTeamMembers(), getOrganizationsNames()]);
        setTeamMembers(members);
        setOrganizations(orgs);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setError({
          title: 'Erro ao carregar dados iniciais',
          message: error?.response?.data?.message || error?.message || 'Não foi possível carregar os dados iniciais',
          statusCode: error?.response?.status,
        });
      } finally {
        setIsLoadingTeamMembers(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchScripts = async () => {
      if (filtersChangedRef.current && currentPage !== 1) {
        return;
      }
      setIsLoading(true);
      try {
        const filters: ScriptFilters = {};

        if (searchStatus.length > 0) {
          filters.status = searchStatus as Array<'PENDING' | 'RUNNING' | 'BLOCKED' | 'FINISHED' | 'FAILED'>;
        }

        if (searchName.trim()) {
          filters.name = searchName.trim();
        }

        if (searchOrganization) {
          filters.organizationId = searchOrganization;
        }

        if (searchAuthor) {
          filters.createdByUserId = searchAuthor;
        }

        if (searchApprover) {
          filters.approvedByUserId = searchApprover;
        }

        if (startDate) {
          filters.startDate = startDate.toISOString();
        }

        if (endDate) {
          filters.endDate = endDate.toISOString();
        }

        const response = await getScripts(
          (items) => {
            setScripts(items);
            return { shouldContinue: false };
          },
          filters,
          currentPage,
        );

        setScripts(response.items ?? []);
        setTotalPages(response.totalPages ?? 1);
      } catch (error) {
        console.error('Error fetching scripts:', error);
        setError({
          title: 'Erro ao carregar scripts',
          message: error?.response?.data?.message || error?.message || 'Não foi possível carregar a lista de scripts',
          statusCode: error?.response?.status,
        });
        setScripts([]);
      } finally {
        setIsLoading(false);
        if (filtersChangedRef.current && currentPage === 1) {
          filtersChangedRef.current = false;
        }
      }
    };

    fetchScripts();
  }, [currentPage, searchStatus, searchName, searchOrganization, searchAuthor, searchApprover, startDate, endDate]);

  const getFinalMinute = (date: Date | null) => {
    if (!date) return null;
    const finalMinute = new Date(date);
    finalMinute.setHours(20);
    finalMinute.setMinutes(59);
    finalMinute.setSeconds(59);
    return finalMinute;
  };

  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(getFinalMinute(end));
  };

  const teamMemberOptions = [
    { value: '', label: 'Todos' },
    ...teamMembers
      .map((member) => ({
        value: member.userId,
        label: member.name || member.email || member.userId,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const organizationOptions = [
    { value: '', label: 'Todas' },
    ...Object.entries(organizations)
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const userNames = useMemo(() => buildUserNamesFromTeamMembers(teamMembers), [teamMembers]);
  const isLoadingUsers = isLoadingTeamMembers;

  const columns = getScriptsListColumns(userNames, isLoadingUsers, onScriptClick, organizations);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        height: { md: 'calc(100vh - 32px)' },
        minHeight: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: titleFontFamily,
            fontSize: '22px',
            fontWeight: 500,
            letterSpacing: '-0.025em',
            color: colors.primary.white,
          }}
        >
          Scripts
        </Typography>
        <Button
          variant="contained"
          disableElevation
          startIcon={<AddIcon />}
          onClick={() => setIsCreateModalOpen(true)}
          sx={primaryButtonSX}
        >
          Novo script canônico
        </Button>
      </Box>

      <FiltersWrapper>
        <FiltersBar
          searchValue={searchName}
          onSearchChange={setSearchName}
          searchPlaceholder="Buscar por nome do script…"
        />
        <DropdownOptionsFilter
          label="Organização"
          value={searchOrganization}
          options={organizationOptions}
          onChange={setSearchOrganization}
          minWidth={190}
        />
        <DateRangeFilter label="Período" startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} />
        <DropdownOptionsFilter
          label="Autor"
          value={searchAuthor}
          options={teamMemberOptions}
          onChange={setSearchAuthor}
        />
        <DropdownOptionsFilter
          label="Aprovador"
          value={searchApprover}
          options={teamMemberOptions}
          onChange={setSearchApprover}
        />
        <CheckboxListFilter
          label="Status"
          value={searchStatus}
          options={[
            { value: 'PENDING', label: 'Pendente' },
            { value: 'RUNNING', label: 'Executando' },
            { value: 'BLOCKED', label: 'Bloqueado' },
            { value: 'FINISHED', label: 'Finalizado' },
            { value: 'FAILED', label: 'Falhou' },
          ]}
          iconConfig={SCRIPT_STATUS_ICON_CONFIG}
          onChange={setSearchStatus}
          minWidth={130}
        />
      </FiltersWrapper>

      <Box
        sx={{
          flex: { xs: 'none', md: '4 1 0' },
          minHeight: { xs: 420, md: 0 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '14px',
          border: `1px solid ${colors.brand.line}`,
          backgroundColor: colors.brand.surface,
        }}
      >
        {isLoading || isLoadingTeamMembers ? (
          <ScriptsListSkeleton rowCount={10} />
        ) : (
          <DataTable
            fillContainer
            data={scripts}
            columns={columns}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onRowClick={onScriptClick}
            stretchRows
          />
        )}
      </Box>

      <Box aria-hidden sx={{ display: { xs: 'none', md: 'block' }, flex: '1 1 0', mt: -2 }} />

      <CreateCanonicalScriptModal
        open={isCreateModalOpen}
        organizations={organizations}
        isLoadingOrganizations={isLoadingTeamMembers}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </Box>
  );
};
